import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

export function DashboardCarousel({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState({
    previous: false,
    next: false,
  });

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    function updateControls() {
      if (!viewport) return;
      const previous = viewport.scrollLeft > 1;
      const next =
        viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 1;
      setScrollable((current) =>
        current.previous === previous && current.next === next
          ? current
          : { previous, next },
      );
    }

    const observer = new ResizeObserver(updateControls);
    observer.observe(viewport);
    viewport.addEventListener("scroll", updateControls, { passive: true });
    return () => {
      observer.disconnect();
      viewport.removeEventListener("scroll", updateControls);
    };
  }, [children]);

  function scroll(direction: number) {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.scrollBy({
      left: direction * viewport.clientWidth,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  }

  return (
    <div className="min-w-0 max-w-full">
      {scrollable.previous || scrollable.next ? (
        <div className="flex justify-end gap-2 px-6">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={`Previous ${label} cards`}
            disabled={!scrollable.previous}
            onClick={() => scroll(-1)}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={`Next ${label} cards`}
            disabled={!scrollable.next}
            onClick={() => scroll(1)}
          >
            <ChevronRight />
          </Button>
        </div>
      ) : null}
      <div
        ref={viewportRef}
        role="region"
        aria-label={`${label} cards`}
        tabIndex={0}
        className="flex min-w-0 max-w-full snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-px-6 px-6 py-6 outline-offset-[-2px] focus-visible:outline-2 focus-visible:outline-ring [&>*]:w-72 [&>*]:max-w-full [&>*]:shrink-0 [&>*]:snap-start"
      >
        {children}
      </div>
    </div>
  );
}
